/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/require-default-props */
import React, { FC, forwardRef, HTMLProps, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Dropdown as RBDropdown } from 'react-bootstrap';

import './Dropdown.scss';

export interface IOptionProp {
  id: string | number;
  label: string;
}

export interface IProps {
  id: string;
  options: IOptionProp[];
  onSelect: (id: string | null, e: React.SyntheticEvent<unknown>) => void;
  value?: IOptionProp;
  label?: string;
  warning?: string;
  helper?: string;
  placeholder?: string;
  disabled?: boolean;
  formComponent?: boolean;
}

const DropdownToggle = forwardRef<HTMLButtonElement, HTMLProps<HTMLButtonElement>>(
  ({ children, onClick, disabled }, ref) => (
    <button
      className="Dropdown-toggle"
      ref={ref as any}
      onClick={(e) => {
        e.preventDefault();
        if (!disabled && onClick) {
          onClick(e);
        }
      }}
    >
      {children}
    </button>
  ),
);

const DropdownMenu = forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>(
  (
    { children, style, 'aria-labelledby': labeledBy, 'aria-hidden': ariaHidden = false, className },
    ref,
  ) => (
    <div
      ref={ref}
      style={style}
      className={classNames('Dropdown-menu', className)}
      aria-labelledby={labeledBy}
      aria-hidden={ariaHidden}
    >
      {children}
    </div>
  ),
);

const Dropdown: FC<IProps> = ({
  id,
  label,
  warning,
  helper,
  value,
  options,
  onSelect,
  placeholder,
  disabled,
  formComponent,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);

  let longestOption;
  const optionElements = options.map((o) => {
    if (!longestOption || longestOption.label.length < o.label.length) {
      longestOption = o;
    }

    return (
      <RBDropdown.Item key={o.id} eventKey={o.id}>
        {o.label}
      </RBDropdown.Item>
    );
  });
  return (
    <>
      <div className={classNames('Dropdown', { 'form-component': formComponent })}>
        <RBDropdown
          className={classNames('Dropdown-base', {
            'is-open': isOpen,
            'has-warning': !!warning,
            disabled,
          })}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          onToggle={(open: boolean) => {
            setIsOpen(open);
          }}
          onSelect={onSelect}
        >
          <DropdownMenu className="Dropdown-menu-dummy" aria-hidden="true">
            {longestOption ? (
              <RBDropdown.Item key={longestOption.id} eventKey={longestOption.id}>
                {longestOption.label}
              </RBDropdown.Item>
            ) : undefined}
          </DropdownMenu>
          <label
            className={classNames('Dropdown-label', {
              show: !!label || !!warning || !!helper,
            })}
            htmlFor={id}
          >
            <div>{label}</div>
            {!!helper && <div className="Dropdown-label-helper">{helper}</div>}
            {/* TODO: Uncomment this when we're ready to move warnings to under the label
          {!!warning && <div className="Dropdown-label-warning">{warning}</div>}
        */}
          </label>
          <RBDropdown.Toggle as={DropdownToggle} id={id} ref={toggleRef} disabled={disabled}>
            <span className={classNames('Dropdown-value', { placeholder: !value })}>
              {options.find((o) => o.id === value?.id)?.label || placeholder || 'Select'}
            </span>
            <span className={classNames('material-icons', 'Dropdown-icon')}>arrow_drop_down</span>
          </RBDropdown.Toggle>
          <RBDropdown.Menu as={DropdownMenu} ref={menuRef}>
            {optionElements}
          </RBDropdown.Menu>
        </RBDropdown>
      </div>
      {/* Get rid of the line below (!!warning) when we move the warning to be under the label */}
      {!!warning && <div className="Dropdown-label-warning">{warning}</div>}
    </>
  );
};

export default Dropdown;
